import type { Pagination } from '../types/api';

export interface TransformConfig {
  enforceUUIDs?: boolean;
  migrateFieldNames?: boolean;
  flattenJsonApi?: boolean;
  addMissingFields?: boolean;
}

const defaultConfig: TransformConfig = {
  enforceUUIDs: false,
  migrateFieldNames: true,
  flattenJsonApi: true,
  addMissingFields: true,
};

// Field name mappings for migration
const fieldNameMappings: Record<string, Record<string, string>> = {
  student: {
    birth_date: 'date_of_birth',
    class_id: 'current_class_id',
  },
  notification: {
    body: 'message',
  },
};

// Default values for missing fields
const defaultFieldValues: Record<string, Record<string, any>> = {
  user: {
    active: true,
    permissions: [],
  },
  student: {
    active: true,
    enrollment_status: 'enrolled',
  },
  camera: {
    active: true,
    health_status: 'unknown',
  },
};

// Transform single resource
export function transformApiResponse<T>(
  response: any,
  resourceType: string,
  config: TransformConfig = defaultConfig
): T {
  if (!response) return response;

  let transformed = { ...response };

  // Flatten JSON:API structure
  if (config.flattenJsonApi && response.attributes) {
    transformed = {
      id: response.id,
      ...response.attributes,
      _type: response.type,
      _relationships: response.relationships,
    };
  }

  // Migrate field names
  if (config.migrateFieldNames && fieldNameMappings[resourceType]) {
    const mappings = fieldNameMappings[resourceType];
    Object.entries(mappings).forEach(([oldName, newName]) => {
      if (oldName in transformed && !(newName in transformed)) {
        transformed[newName] = transformed[oldName];
      }
    });
  }

  // Add missing fields with defaults
  if (config.addMissingFields && defaultFieldValues[resourceType]) {
    const defaults = defaultFieldValues[resourceType];
    Object.entries(defaults).forEach(([field, value]) => {
      if (!(field in transformed)) {
        transformed[field] = value;
      }
    });
  }

  // Ensure UUID format for IDs (development only)
  if (config.enforceUUIDs && process.env.NODE_ENV === 'development') {
    if (transformed.id && typeof transformed.id === 'number') {
      console.warn(`Numeric ID found for ${resourceType}:`, transformed.id);
    }
  }

  return transformed as T;
}

// Transform list response
export function transformListResponse<T>(
  response: any,
  resourceType: string,
  config: TransformConfig = defaultConfig
): {
  items: T[];
  pagination?: Pagination;
} {
  if (!response) return { items: [] };

  let items: T[] = [];
  let pagination: Pagination | undefined;

  // Extract items from different response formats
  if (Array.isArray(response)) {
    items = response;
  } else if (response.data && Array.isArray(response.data)) {
    items = response.data;
  } else if (response.items && Array.isArray(response.items)) {
    items = response.items;
  }

  // Transform each item
  items = items.map(item => transformApiResponse<T>(item, resourceType, config));

  // Extract pagination
  if (response.pagination) {
    pagination = response.pagination;
  } else if (response.meta?.pagination) {
    pagination = response.meta.pagination;
  } else if (response.meta) {
    // Build pagination from meta fields
    const { current_page, total_pages, total_count, per_page } = response.meta;
    if (current_page !== undefined && total_pages !== undefined) {
      pagination = {
        current_page,
        total_pages,
        total_count: total_count || 0,
        per_page: per_page || 20,
        has_previous: current_page > 1,
        has_next: current_page < total_pages,
      };
    }
  }

  return { items, pagination };
}

// Batch transform helper
export function batchTransform<T>(
  items: any[],
  resourceType: string,
  config?: TransformConfig
): T[] {
  return items.map(item => transformApiResponse<T>(item, resourceType, config));
}

// Field migration helper
export function migrateFieldNames(
  data: any,
  mappings: Record<string, string>
): any {
  const migrated = { ...data };
  
  Object.entries(mappings).forEach(([oldName, newName]) => {
    if (oldName in migrated) {
      migrated[newName] = migrated[oldName];
      if (oldName !== newName) {
        delete migrated[oldName];
      }
    }
  });
  
  return migrated;
}

// Export utilities
export function registerFieldMapping(
  resourceType: string,
  oldField: string,
  newField: string
): void {
  if (!fieldNameMappings[resourceType]) {
    fieldNameMappings[resourceType] = {};
  }
  fieldNameMappings[resourceType][oldField] = newField;
}

export function registerDefaultValue(
  resourceType: string,
  field: string,
  defaultValue: any
): void {
  if (!defaultFieldValues[resourceType]) {
    defaultFieldValues[resourceType] = {};
  }
  defaultFieldValues[resourceType][field] = defaultValue;
}